import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { defaultEQM2050_R01Dto, EQM2050_R01Dto } from './Dto_EQM2050_R01';
import { normalizeValueIndex } from './Utils_EQM2050_R01';

export interface EQM2050_R01Filter {
	compCd: string;				// 사업장
	inspectionMonth: string;	// 년월 (연월 형식: yyyy-MM)
}

export interface Store_EQM2050_R01 {
	// === 필터 관련 ===
	EQM2050_R01Filter: EQM2050_R01Filter;
	EQM2050_R01PastFilter: EQM2050_R01Filter;
	setEQM2050_R01Filter: (filter: Partial<EQM2050_R01Filter>) => void;
	setEQM2050_R01PastFilter: (filter: Partial<EQM2050_R01Filter>) => void;

	// === 리스트 관련 ===
	EQM2050_R01List: EQM2050_R01Dto[];
	setEQM2050_R01List: (list: EQM2050_R01Dto[]) => void;
	insertEQM2050_R01List: (data: EQM2050_R01Dto[], index?: number) => void;
	removeEQM2050_R01List: (valueIndex: number) => void;
	updateEQM2050_R01List: (valueIndex: number, data: EQM2050_R01Dto) => void;
	originalEQM2050_R01List: EQM2050_R01Dto[];
	setOriginalEQM2050_R01List: (list: EQM2050_R01Dto[]) => void;
	maxValueIndex: number; // 최대 valueIndex 캐싱 (성능 최적화)

	// === 선택 관련 ===
	selectedEQM2050_R01List: number[];
	setSelectedEQM2050_R01List: (idList: number[]) => void;
	addSelectedEQM2050_R01List: (idList: number[]) => void;
	removeSelectedEQM2050_R01List: (idList?: number[]) => void;
	initSelectedEQM2050_R01List: () => void;
	allSelectedEQM2050_R01List: () => void;

	// === 삭제 관련 ===
	deletedEQM2050_R01List: number[];
	addDeletedEQM2050_R01List: (idList: number[]) => void;
	removeDeletedEQM2050_R01List: (idList?: number[]) => void;

	// === 편집 관련 ===
	editEQM2050_R01: EQM2050_R01Dto | null;
	setEditEQM2050_R01: (item: EQM2050_R01Dto | null) => void;

	// === AG Grid 컬럼 상태 관리 ===
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	// === 전체 초기화 ===
	initData: () => void;
}

// 현재 월을 yyyy-MM 형식으로 반환
const getCurrentMonth = (): string => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	return `${year}-${month}`;
};

export const defaultEQM2050_R01Filter: EQM2050_R01Filter = {
	compCd: '',
	inspectionMonth: getCurrentMonth(),
};

/**
 * Zustand 스토어 정의
 */
export const useEQM2050_R01Store = create<Store_EQM2050_R01>((set) => {
	/**
	 * 공통 필터 설정 함수 생성
	 */
	const createFilterSetter = (filterKey: keyof Pick<Store_EQM2050_R01, 'EQM2050_R01Filter' | 'EQM2050_R01PastFilter'>) =>
		(filter: Partial<EQM2050_R01Filter>) => set((state) => ({
			...state,
			[filterKey]: { ...state[filterKey], ...filter }
		}));

	return {
		// === 필터 관련 ===
		EQM2050_R01Filter: defaultEQM2050_R01Filter,
		EQM2050_R01PastFilter: defaultEQM2050_R01Filter,
		setEQM2050_R01Filter: createFilterSetter('EQM2050_R01Filter'),
		setEQM2050_R01PastFilter: createFilterSetter('EQM2050_R01PastFilter'),

		// === 리스트 관련 ===
		EQM2050_R01List: [],
		setEQM2050_R01List: (list: EQM2050_R01Dto[]) => set((state) => {
			const newList: EQM2050_R01Dto[] = [];
			let fallbackIndex = 1;
			let maxValueIndex = 0; // 최대값 추적

			list.forEach((item) => {
				// 유틸 함수로 정규화
				const normalizedValueIndex = normalizeValueIndex(item.valueIndex, fallbackIndex);

				// 최대값 업데이트
				if (normalizedValueIndex > maxValueIndex) {
					maxValueIndex = normalizedValueIndex;
				}

				newList.push({
					...defaultEQM2050_R01Dto,
					...item,
					valueIndex: normalizedValueIndex,
					orderIndex: item.orderIndex > -1 ? item.orderIndex : fallbackIndex,
					orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
				});
				fallbackIndex++;
			});

			return { 
				...state, 
				EQM2050_R01List: newList,
				maxValueIndex: maxValueIndex // 최대값 캐싱
			};
		}),
	insertEQM2050_R01List: (data: EQM2050_R01Dto[], index?: number) => set((state) => {
		const currentList = [...state.EQM2050_R01List];
		let maxValueIndex = state.maxValueIndex + 1;

		// InsertRowHandler는 rowData.valueIndex를 전달함
		// 해당 valueIndex를 가진 행의 배열 인덱스를 찾음
		const targetIndex = index !== undefined 
			? currentList.findIndex(item => item.valueIndex === index)
			: -1;

		// 실제 삽입 위치: 찾은 행의 다음 위치
		const insertPosition = targetIndex >= 0 ? targetIndex + 1 : currentList.length;
		
		// 클릭한 행의 orderIndex를 가져옴 (정렬 유지를 위해)
		const targetOrderIndex = targetIndex >= 0 ? currentList[targetIndex].orderIndex : insertPosition + 1;

		// 새 행 생성
		const preparedData = data.map((item, idx) => {
			// 유틸 함수로 정규화 (maxValueIndex를 fallback으로 사용)
			const valueIndex = normalizeValueIndex(item.valueIndex, maxValueIndex++);

			return {
				...defaultEQM2050_R01Dto,
				...item,
				valueIndex,
				orderIndex: targetOrderIndex,  // PRD1010_R01과 동일: 항상 클릭한 행의 orderIndex 사용
				orderIndexSeq: 0,              // EQM2050_R01은 단순 리스트이므로 0으로 고정
				inserted: item.inserted ?? true,
			};
		});

		// 배열의 실제 인덱스 위치에 삽입
		currentList.splice(insertPosition, 0, ...preparedData);

		return { 
			...state, 
			EQM2050_R01List: currentList,
			maxValueIndex: maxValueIndex - 1 // 마지막 사용된 값으로 업데이트
		};
	}),
		removeEQM2050_R01List: (valueIndex: number) => set((state) => ({
			...state,
			EQM2050_R01List: state.EQM2050_R01List.filter((item) => item.valueIndex !== valueIndex),
		})),
		updateEQM2050_R01List: (valueIndex: number, data: EQM2050_R01Dto) => set((state) => {
			const _list = state.EQM2050_R01List.map(item =>
				item.valueIndex === valueIndex ? data : item
			);
			return { ...state, EQM2050_R01List: _list };
		}),
		originalEQM2050_R01List: [],
		setOriginalEQM2050_R01List: (list: EQM2050_R01Dto[]) => set({ originalEQM2050_R01List: list }),

		// === 선택 관련 ===
		selectedEQM2050_R01List: [],
		setSelectedEQM2050_R01List: (idList: number[]) => set({ selectedEQM2050_R01List: idList }),
		addSelectedEQM2050_R01List: (idList: number[]) => set((state) => {
			const newSet = new Set([...state.selectedEQM2050_R01List, ...idList]);
			return {
				...state,
				selectedEQM2050_R01List: Array.from(newSet)
			};
		}),
		removeSelectedEQM2050_R01List: (idList?: number[]) => set((state) => {
			if (idList) {
				return {
					...state,
					selectedEQM2050_R01List: state.selectedEQM2050_R01List.filter(id => !idList.includes(id))
				};
			}
			return { ...state, selectedEQM2050_R01List: [] };
		}),
		initSelectedEQM2050_R01List: () => set({ selectedEQM2050_R01List: [], editEQM2050_R01: defaultEQM2050_R01Dto }),
		allSelectedEQM2050_R01List: () => set((state) => ({
			...state,
			selectedEQM2050_R01List: state.EQM2050_R01List.map(item => item.valueIndex)
		})),

		// === 삭제 관련 ===
		deletedEQM2050_R01List: [],
		addDeletedEQM2050_R01List: (idList: number[]) => set((state) => {
			// valueIndex 기준으로 삭제 표시 (숫자로 저장)
			const newSet = new Set([...state.deletedEQM2050_R01List, ...idList]);
			return {
				...state,
				deletedEQM2050_R01List: Array.from(newSet)
			};
		}),
		removeDeletedEQM2050_R01List: (idList?: number[]) => set((state) => {
			if (idList) {
				return {
					...state,
					deletedEQM2050_R01List: state.deletedEQM2050_R01List.filter(id => !idList.includes(id))
				};
			}
			return { ...state, deletedEQM2050_R01List: [] };
		}),

		// === 편집 관련 ===
		editEQM2050_R01: defaultEQM2050_R01Dto,
		setEditEQM2050_R01: (item: EQM2050_R01Dto | null) => set((state) => {
			let editData = defaultEQM2050_R01Dto;
			if (item) {
				editData = { ...item };
			}
			return { editEQM2050_R01: editData };
		}),

		// === AG Grid 컬럼 상태 관리 ===
		columnState: null,
		setColumnState: (state) => set({ columnState: state }),

		scrollTop: null,
		setScrollTop: (top: number | null) => set({ scrollTop: top }),

		allCount: 0,
		setAllCount: (allCount: number) => set({ allCount }),

		// === 전체 초기화 ===
		initData: () => set(() => ({
			EQM2050_R01Filter: defaultEQM2050_R01Filter,
			EQM2050_R01PastFilter: defaultEQM2050_R01Filter,
			EQM2050_R01List: [],
			originalEQM2050_R01List: [],
			selectedEQM2050_R01List: [],
			editEQM2050_R01: defaultEQM2050_R01Dto,
			deletedEQM2050_R01List: [],
			columnState: null,
			allCount: 0,
			maxValueIndex: 0,
			scrollTop: null,
		})),
		
		// === 최대값 캐싱 ===
		maxValueIndex: 0,
	};
});

export interface Store_EQM2050_R01_GridCalendarState {
	// ====== EQM2050_R01 GridCalendar 상태 ======
	EQM2050_R01GridCalendarState: {
		pickerVisible: boolean;
		pickerPosition: { top: number; left: number };
		targetRowIndex: number | null;
		targetField: string | null;
		targetDate: Date | undefined;
	};
	setEQM2050_R01GridCalendarState: (state: Partial<Store_EQM2050_R01_GridCalendarState['EQM2050_R01GridCalendarState']>) => void;
	initEQM2050_R01GridCalendarState: () => void;
}

export const useEQM2050_R01Store_GridCalendar = create<Store_EQM2050_R01_GridCalendarState>((set) => ({
	// ====== GridCalendar 상태 ======
	EQM2050_R01GridCalendarState: {
		pickerVisible: false,
		pickerPosition: { top: 0, left: 0 },
		targetRowIndex: null,
		targetField: null,
		targetDate: undefined
	},
	setEQM2050_R01GridCalendarState: (newState: Partial<Store_EQM2050_R01_GridCalendarState['EQM2050_R01GridCalendarState']>) => set((state) => ({
		...state,
		EQM2050_R01GridCalendarState: { ...state.EQM2050_R01GridCalendarState, ...newState }
	})),
	initEQM2050_R01GridCalendarState: () => set((state) => ({
		...state,
		EQM2050_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined
		}
	})),
}));

